import * as React from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import dateUtil from '../../../utils/dateUtil';
import ShiftBlock from '../../../components/ShiftBlock';
import { colors } from '../../employees/styles/personalakteStyles';
import { SwapRequestMinimal } from '../services/SwapService';
import { UnifiedEvent } from '../../events/types';
import { eventsOnDate } from '../../events/services/unifyEvents';
import ShiftEventIndicator from '../../events/components/ShiftEventIndicator';

const EMPTY_EVENTS: UnifiedEvent[] = [];

type Absence = { id:number; employee:number; status:string; start_datetime:string; end_datetime:string; absence_type?:string };

interface ShiftTableProps {
  tableShifts: any[];
  startDate: Date;
  draggableWAList: any[];
  allEmployees: any[];
  weekAbsences: Absence[];
  weatherData: Record<string, any>;
  weatherByLocation: Record<number, Record<string, any>>;
  uniqueLocations: { id: number; name: string }[];
  countsByDate: Record<string, { red: number; orange: number }>;
  activeEmployeeId: number | null;
  dragging: any;
  dropTarget: any;
  swapRequests: SwapRequestMinimal[];
  hiddenWorkAreas: number[];
  weekEvents?: UnifiedEvent[];
  onShiftDragEnd: (result: any) => void;
  onShiftClick: (shiftId: number) => void;
  onEditClick: (shift: any) => void;
  onLongPressStart: (shift: any, dateStr: string) => (ev: any) => void;
  onToggleAssignment: (shiftId: number, empId: number) => void;
  onQuickAdd: (dateStr: string, workAreaId: number) => void;
  setDropTarget: (target: { date: string; waId: number } | null) => void;
  setAbsModal: (modal: { open: boolean; date?: string; type?: 'red' | 'orange'; anchor?: { top: number; left: number } }) => void;
  formatHeader: (dateStr: string) => string;
  getWeatherIcon: (key: string, precipProbability?: number) => string;
}

function ShiftTable({
  tableShifts,
  startDate,
  draggableWAList,
  allEmployees,
  weekAbsences,
  weatherData,
  weatherByLocation,
  uniqueLocations,
  countsByDate,
  activeEmployeeId,
  dragging,
  dropTarget,
  swapRequests,
  hiddenWorkAreas,
  weekEvents = EMPTY_EVENTS,
  onShiftDragEnd,
  onShiftClick,
  onEditClick,
  onLongPressStart,
  onToggleAssignment,
  onQuickAdd,
  setDropTarget,
  setAbsModal,
  formatHeader,
  getWeatherIcon,
}: ShiftTableProps) {
  if (!tableShifts || tableShifts.length === 0) {
    return <div>No data loaded</div>;
  }

  const monday = dateUtil.getMonday(startDate);
  const daysOfWeek: string[] = [];

  for (let i = 0; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    const year = day.getFullYear();
    const month = String(day.getMonth() + 1).padStart(2, '0');
    const date = String(day.getDate()).padStart(2, '0');
    daysOfWeek.push(`${year}-${month}-${date}`);
  }

  const waIdInOrder = draggableWAList.map(wa => wa.id);

  const employeeStats: Record<number, { assignedHours: number; targetHours: number; appCount: number; weekendAppCount: number }> = {};
  allEmployees.forEach((emp: any) => {
    employeeStats[emp.id] = {
      assignedHours: 0,
      targetHours: Number(emp.contract_hours_per_week ?? emp.min_hours ?? 10) || 10,
      appCount: 0,
      weekendAppCount: 0,
    };
  });
  const isWeekendShift = (sh: any): boolean => {
    const localDate = dateUtil.convertUTCToTenantTime(sh.start_time) || new Date(sh.start_time);
    const day = localDate.getDay();
    if (day === 6 || day === 0) return true; // Saturday, Sunday all day
    if (day === 5 && localDate.getHours() >= 17) return true; // Friday evening
    return false;
  };
  tableShifts.forEach((dayObj: any) => {
    if (!dayObj.shifts) return;
    dayObj.shifts.forEach((sh: any) => {
      const durationH = (new Date(sh.end_time).getTime() - new Date(sh.start_time).getTime()) / 3600000;
      (sh.assigned_employees || []).forEach((aEmp: any) => {
        if (!employeeStats[aEmp.id]) {
          employeeStats[aEmp.id] = { assignedHours: 0, targetHours: 10, appCount: 0, weekendAppCount: 0 };
        }
        employeeStats[aEmp.id].assignedHours += durationH;
      });
      const weekend = isWeekendShift(sh);
      (sh.applications || []).forEach((app: any) => {
        const empId = Number(app.employee);
        if (!employeeStats[empId]) {
          employeeStats[empId] = { assignedHours: 0, targetHours: 10, appCount: 0, weekendAppCount: 0 };
        }
        employeeStats[empId].appCount += 1;
        if (weekend) {
          employeeStats[empId].weekendAppCount += 1;
        }
      });
    });
  });

  const isActiveEmployeeAbsentOnDate = (dateStr: string): boolean => {
    if (!activeEmployeeId) return false;
    const dayStart = new Date(`${dateStr}T00:00:00`).getTime();
    const dayEnd = new Date(`${dateStr}T23:59:59`).getTime();
    return weekAbsences.some(a => {
      if (a.employee !== activeEmployeeId) return false;
      const statusUpper = (a.status || '').toUpperCase();
      if (statusUpper !== 'APPROVED' && statusUpper !== 'REQUESTED') return false;
      const absStart = dateUtil.createLocalDate(a.start_datetime).getTime();
      const absEnd = dateUtil.createLocalDate(a.end_datetime).getTime();
      return absStart <= dayEnd && absEnd > dayStart;
    });
  };

  const extractTime = (isoStr: string) => {
    const dt = dateUtil.convertUTCToTenantTime(isoStr) || new Date(isoStr);
    return `${dt.getHours().toString().padStart(2, '0')}:${dt.getMinutes().toString().padStart(2, '0')}`;
  };

  return (
    <DragDropContext onDragEnd={onShiftDragEnd}>
    <div className="glass-card" style={{ padding: 0, margin: '0 16px 16px 12px', flex: 1, minHeight: 0, overflow: 'auto', scrollbarWidth: 'thin' }}>
    <table
      style={{
        borderCollapse:'collapse',
        width:'100%',
        marginTop:'0px',
        fontSize:'0.85rem',
        tableLayout:'fixed'
      }}
    >
      <thead>
        <tr style={{ borderBottom:'1px solid rgba(0, 0, 0, 0.08)' }}>
          {daysOfWeek.map((dateStr, colIdx) => {
            const c = countsByDate[dateStr] || { red:0, orange:0 };

            const activeEmpAbsence = activeEmployeeId ? weekAbsences.find(a => {
              if (a.employee !== activeEmployeeId) return false;
              const dayStart = new Date(`${dateStr}T00:00:00`).getTime();
              const dayEnd = new Date(`${dateStr}T23:59:59`).getTime();
              const absStart = dateUtil.createLocalDate(a.start_datetime).getTime();
              const absEnd = dateUtil.createLocalDate(a.end_datetime).getTime();
              return absStart <= dayEnd && absEnd > dayStart;
            }) : null;

            const activeEmpStatus = (activeEmpAbsence?.status || '').toUpperCase();
            const hasActiveEmpApproved = activeEmpStatus === 'APPROVED';
            const hasActiveEmpRequested = activeEmpStatus === 'REQUESTED';

            const headerDayEvents = eventsOnDate(weekEvents, dateStr);
            const hasHeaderEvents = headerDayEvents.length > 0;
            const hasHeaderHoliday = headerDayEvents.some(e => e.isHoliday);

            return (
              <th
                key={dateStr}
                style={{
                  border: 'none',
                  padding: '6px 8px 4px',
                  textAlign: 'center',
                  fontWeight: 600,
                  fontSize: '11px',
                  background: '#f8f9fb',
                  color: colors.textBody,
                  letterSpacing: '0.3px',
                  boxSizing: 'border-box',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  borderRadius: colIdx === 0 ? '10px 0 0 0' : colIdx === daysOfWeek.length - 1 ? '0 10px 0 0' : '0',
                }}
              >
                <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                <div style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', gap:6, flexWrap:'nowrap' }}>
                  <ShiftEventIndicator events={headerDayEvents}>
                    <span style={{
                      whiteSpace:'nowrap',
                      ...(hasHeaderEvents ? {
                        color: hasHeaderHoliday ? '#16a34a' : '#3b82f6',
                        fontWeight: 700,
                      } : {}),
                    }}>{formatHeader(dateStr)}</span>
                  </ShiftEventIndicator>
                                      {(c.red>0 || c.orange>0 || hasActiveEmpApproved || hasActiveEmpRequested) && (
                    <span style={{ display:'flex', gap:4, alignItems:'center', marginLeft:0 }}>
                      {(c.red>0 || hasActiveEmpApproved) && (
                        <span
                          role="button"
                          onClick={(e)=>{
                            const el = e.currentTarget as HTMLElement; const rect = el.getBoundingClientRect();
                            const modalWidth = 320; const margin = 8;
                            let left = rect.left + window.scrollX; let top = rect.bottom + window.scrollY + 6;
                            const maxLeft = window.scrollX + window.innerWidth - modalWidth - margin;
                            if (left > maxLeft) left = Math.max(window.scrollX + margin, maxLeft);
                            setAbsModal({ open:true, date:dateStr, type:'red', anchor:{ top, left } });
                          }}
                          style={{
                            minWidth: 18,
                            height: 16,
                            padding: '0 5px',
                            borderRadius: 6,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            fontWeight: 700,
                            background: colors.danger,
                            color: '#ffffff',
                            border: `1px solid ${colors.danger}`,
                            cursor: 'pointer',
                            ...(hasActiveEmpApproved ? {
                              boxShadow: '0 0 8px 2px rgba(220, 53, 69, 0.6)',
                              animation: 'pulse-glow 3.5s ease-in-out forwards',
                            } : {}),
                          }}
                        >{c.red > 0 ? c.red : '!'}</span>
                      )}
                      {(c.orange>0 || (hasActiveEmpRequested && !hasActiveEmpApproved)) && (
                        <span
                          role="button"
                          onClick={(e)=>{
                            const el = e.currentTarget as HTMLElement; const rect = el.getBoundingClientRect();
                            const modalWidth = 320; const margin = 8;
                            let left = rect.left + window.scrollX; let top = rect.bottom + window.scrollY + 6;
                            const maxLeft = window.scrollX + window.innerWidth - modalWidth - margin;
                            if (left > maxLeft) left = Math.max(window.scrollX + margin, maxLeft);
                            setAbsModal({ open:true, date:dateStr, type:'orange', anchor:{ top, left } });
                          }}
                          style={{
                            minWidth: 18,
                            height: 16,
                            padding: '0 5px',
                            borderRadius: 6,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 11,
                            fontWeight: 700,
                            background: colors.warning,
                            color: '#ffffff',
                            border: `1px solid ${colors.warning}`,
                            cursor: 'pointer',
                            ...(hasActiveEmpRequested && !hasActiveEmpApproved ? {
                              boxShadow: '0 0 6px 1px rgba(245, 158, 11, 0.5)',
                            } : {}),
                          }}
                        >{c.orange > 0 ? c.orange : '?'}</span>
                      )}
                    </span>
                  )}
                </div>
                {(() => {
                  const w = weatherData[dateStr];
                  if (!w) return null;
                  return (
                    <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2, whiteSpace: 'nowrap' }}>
                      {getWeatherIcon(w.icon_key, w.precip_probability_max)} {Math.round(w.temp_max)}°/{Math.round(w.temp_min)}°
                      {w.precip_probability_max > 0 && (
                        <span style={{ marginLeft: 4, color: w.precip_probability_max >= 50 ? colors.slate : colors.textLight }}>
                          {w.precip_probability_max}%
                        </span>
                      )}
                    </div>
                  );
                })()}
                </div>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {(() => {
          return waIdInOrder.map((waId, waIndex) => {
            const waInfo = draggableWAList.find(w => w.id === waId);

            const maxShiftsPerDay = daysOfWeek.reduce((max, dateStr) => {
              const dayObj = tableShifts.find(x => x.date === dateStr);
              const count = (dayObj?.shifts || []).filter(s => s.work_area === waId).length;
              return Math.max(max, count);
            }, 0);

            const slots: string[] = ['__all__'];

            return slots.map((slot, slotIdx) => (
              <tr key={`${waId}-${slot}`} style={{
                border:'none',
                ...(waIndex === 0 && slotIdx === 0 ? { borderTop: '8px solid transparent' } : {})
              }}>
                {daysOfWeek.map(dateStr => {
                  const dayObj = tableShifts.find(x => x.date === dateStr);
                  const shiftsForCell = (() => {
                    const waShifts = (dayObj?.shifts || []).filter(s => s.work_area === waId);
                    if (slot === '__all__') return waShifts;
                    const timeKey = slot.replace('__ts_', '').replace('__', '');
                    return waShifts.filter(s => {
                      const key = `${extractTime(s.start_time)}|${extractTime(s.end_time)}`;
                      return key === timeKey;
                    });
                  })();
                  const enriched = shiftsForCell
                    .sort((a, b) => {
                      const startA = extractTime(a.start_time);
                      const startB = extractTime(b.start_time);
                      if (startA !== startB) return startA.localeCompare(startB);
                      return extractTime(a.end_time).localeCompare(extractTime(b.end_time));
                    })
                    .map(s => ({
                      ...s,
                      work_area__color: s.work_area__color || waInfo?.color,
                      work_area__name: s.work_area__name || waInfo?.name,
                    }));

                  if (enriched.length === 0) {
                    return (
                      <td
                        key={dateStr}
                        onClick={() => onQuickAdd(dateStr, waId)}
                        title="Click to add shift"
                        style={{
                          border:'none',
                          verticalAlign:'top',
                          padding:'0',
                          width: '14%',
                          cursor: 'pointer'
                        }}
                      >
                        <Droppable droppableId={`cell|${dateStr}|${waId}|${slotIdx}`}>
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              style={{
                                width: '100%',
                                minHeight: '4px',
                                padding: '1px',
                                outline: (dropTarget && dropTarget.date===dateStr && dropTarget.waId===waId) ? `2px dashed ${colors.navy}` : 'none'
                              }}
                              onMouseEnter={()=> dragging && setDropTarget({ date: dateStr, waId })}
                              onTouchStart={()=> dragging && setDropTarget({ date: dateStr, waId })}
                            >
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={dateStr}
                      onClick={() => onQuickAdd(dateStr, waId)}
                      title="Click to add shift"
                      style={{ border:'none', verticalAlign:'top', padding:'0', width: '14%', height:'100%', cursor: 'pointer' }}
                      data-testid={`shift-cell-${dateStr}-${waId}`}
                    >
                      <Droppable droppableId={`cell|${dateStr}|${waId}|${slotIdx}`}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              gap: '8px',
                              padding: '4px',
                              height: '100%'
                            }}
                            onMouseEnter={()=> dragging && setDropTarget({ date: dateStr, waId })}
                            onTouchStart={()=> dragging && setDropTarget({ date: dateStr, waId })}
                          >
                            {enriched.map((shift, idx) => (
                              <Draggable key={`shift|${shift.id}`} draggableId={`shift|${shift.id}`} index={idx}>
                                {(provided2) => (
                                  <div ref={provided2.innerRef} {...provided2.draggableProps} {...provided2.dragHandleProps} style={{ ...provided2.draggableProps.style, width: '100%' }}>
                                    <ShiftBlock
                                      shift={shift}
                                      activeEmployeeId={activeEmployeeId}
                                      onShiftClick={() => onShiftClick(shift.id)}
                                      onEditClick={() => onEditClick(shift)}
                                      onLongPress={onLongPressStart(shift, dateStr)}
                                      disableClicks={!!dragging}
                                      isActiveEmployeeAbsent={isActiveEmployeeAbsentOnDate(dateStr)}
                                      onAssignClick={(shiftId, empId) => onToggleAssignment(shiftId, empId)}
                                      allEmployees={allEmployees}
                                      swapRequests={swapRequests}
                                      employeeStats={employeeStats}
                                    />
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </td>
                  );
                })}
              </tr>
            ));
          });
        })()}
      </tbody>
    </table>
    </div>
    </DragDropContext>
  );
}

export default ShiftTable;
